import React, { useEffect, useState } from 'react';
import TopNavigation, {
  TopNavigationProps,
} from '@amzn/awsui-components-react/polaris/top-navigation';
import { useLocation, useSearchParams } from 'react-router-dom';
import { externalLinks } from '../data/externalLinks';
import { publishKatalMetric } from './katalAnalytics';
import { useSpaWrapper } from './SpaLink';
import { useUser } from '../hooks/user';
import { featureIsEnabled, useFeatures } from '../hooks/features';
import { IsItDownFlashbar } from '../utils/isItDownBanners';
import { LanguagePickerPanel } from './localization/languagePickerPanel';
import {
  CHANGE_LANGUAGE_NAV_ITEM_ID,
  LOCALIZATION,
  SUGGEST_TRANSLATION_FIX_NAV_ITEM_ID,
} from '../utils/constants';
import { useLocalizedText } from '../hooks/localizedText';
import { useTranslationFeedbackHref } from '../hooks/translationFeedback';

export interface NewTopNavProps {
  /**
   * Optional navigation guard set by the current page (via outlet context in App.tsx).
   * Called before the logo navigates home — returns true to allow, false to block.
   * Pages with unsaved changes (create/edit event wizard) set this so clicking the logo
   * mid-wizard shows their discard modal instead of silently discarding work
   * (Cloudscape unsaved-changes pattern). Mirrors SiteNavigation and SiteBreadcrumbs.
   */
  navigationGuard: ((href: string) => boolean) | null;
}

/**
 * Simplified TopNavigation for the new navigation redesign.
 * Uses the default dark Cloudscape TopNavigation styling.
 * Only contains: logo, Help dropdown, and user identity — which doubles as the language
 * picker's entry point when Localization is on, since the side nav owns everything structural.
 */
export function NewTopNav({ navigationGuard }: NewTopNavProps) {
  const spaWrapper = useSpaWrapper();
  const { t } = useLocalizedText(['translation']);
  const { userId } = useUser();
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const { pathname } = useLocation();
  const [languagePanelOpen, setLanguagePanelOpen] = useState(false);
  const localizationEnabled = featureIsEnabled(LOCALIZATION, features, searchParams);
  const translationFeedbackHref = useTranslationFeedbackHref();

  // Closes the panel when the user navigates, so it does not float over the next page.
  useEffect(() => {
    setLanguagePanelOpen(false);
  }, [pathname]);

  const navigateHome = spaWrapper({ url: '/' });

  // Changing language is the only thing in this menu, so without it the alias is just a label.
  // The side nav owns structural navigation, so this menu is the only place the picker can live.
  const userUtility: TopNavigationProps.Utility = localizationEnabled
    ? {
        type: 'menu-dropdown',
        text: userId + '@',
        ariaLabel: userId + '@',
        onItemClick: ({ detail }) => {
          publishKatalMetric('Click', 'nav_' + detail.id);
          if (detail.id === CHANGE_LANGUAGE_NAV_ITEM_ID) {
            setLanguagePanelOpen(true);
          }
        },
        items: [
          {
            id: CHANGE_LANGUAGE_NAV_ITEM_ID,
            text: t('topnav.changeLanguage'),
            iconName: 'globe',
          },
        ],
      }
    : {
        type: 'button',
        text: userId + '@',
        variant: 'link',
      };

  const utilities: TopNavigationProps.Utility[] = [
    {
      type: 'menu-dropdown',
      iconName: 'support',
      ariaLabel: t('topnav.help'),
      onItemFollow: ({ detail }) => {
        publishKatalMetric('Click', 'nav_' + detail.id);
      },
      items: [
        {
          id: 'question',
          text: t('topnav.question'),
          href: externalLinks.support.question,
          external: true,
        },
        {
          id: 'featureRequest',
          text: t('topnav.featureRequest'),
          href: externalLinks.support.feature,
          external: true,
        },
        {
          id: 'ticket',
          text: t('topnav.ticket'),
          href: externalLinks.support.ticket,
          external: true,
        },
        ...(translationFeedbackHref
          ? [
              {
                id: SUGGEST_TRANSLATION_FIX_NAV_ITEM_ID,
                text: t('translationFeedback.linkLabel'),
                href: translationFeedbackHref,
                external: true,
              },
            ]
          : []),
      ],
    },
    userUtility,
  ];

  return (
    <>
      <IsItDownFlashbar />
      {/* Anchors the language panel, which is absolutely positioned so opening it floats over the
          page instead of pushing every route's content down. */}
      <div className="vc-top-nav">
        <TopNavigation
          identity={{
            href: '/',
            onFollow: (event) => {
              // Consult the current page's unsaved-changes guard before navigating home.
              // If it blocks (returns false), the page shows its own discard modal and we abort,
              // mirroring SiteNavigation and SiteBreadcrumbs.
              if (navigationGuard && !navigationGuard('/')) {
                event.preventDefault();
                return;
              }
              navigateHome(event);
            },
            logo: {
              src: '/red-velvet-logo.svg',
              alt: t('programName'),
            },
          }}
          utilities={utilities}
        />
        {localizationEnabled && languagePanelOpen && (
          <LanguagePickerPanel onDismiss={() => setLanguagePanelOpen(false)} />
        )}
      </div>
    </>
  );
}
